// Aggregates family (Dashboard, Insights/analytics, Buildings detail) -> NomOS (SEN-395, TZ-2).
//
// Rule 1: NomOS is the only engine. Every number below is a NomOS query result (`POST /queries`) or a record
// attribute; this module only maps ids, windows and envelopes. Where NomOS cannot answer, the field is left
// null/zero and the gap is named next to the code ("GAP:") and in the porter report.
//
// Semantics learned from Sentinel (analytics.py / energy_metrics_service.py):
//  - headline kWh = NET (main-meter import minus on-site generation)  -> metric `net_kwh`
//  - EUI = annualised, area-weighted, each site over its own observed days -> `eui_per_building`
//  - cost = `estimated_cost_eur`; carbon = `estimated_carbon_kg` (NomOS: ONE factor per site, Sentinel: per fuel)
//  - Sentinel windows are inclusive local dates [start, end]; NomOS windows are [period_start, period_end)
//    instants, so end+1 day at local midnight in the tenant's zone (settings.timezone; bucket_date is the local day).
import type {
  ConsumptionBreakdownResponse,
  KPIData,
  KPIResponse,
  PeriodSelectorSeriesResponse,
  WeatherHistoryPoint,
  WeatherHistoryResponse,
} from '@/types';
import { localMidnight, tenantZone } from '../tz';
import {
  registerTenantCache,
  nomosFetch,
  queryRecords,
  runQuery,
  scalar,
  toAxiosError,
  type NomosEnvelope,
  type NomosFilter,
  type ReadPriority,
} from '../client';
import type { AnyFn, NomosRegistry } from '../registry';
import { countsInPeriod, METER_WINDOW_FIELDS } from './meterWindow';
import { partialNote, reasonOf, unknownReason } from '@poiesis/views/answerNotes';

/** Owner ruling 2026-10-20: consumption totals count IMPORTED energy only (Aion `kwh`). Export (`kwh_export`,
 *  solar production) never subtracts from consumption; it is its own quantity, shown separately. */
const CONSUMPTION = 'kwh';
const CONSUMPTION_EUI = 'eui_gross_per_building';

// ---------------------------------------------------------------------------------------------------------------
// Transport helpers
// ---------------------------------------------------------------------------------------------------------------

type Row = Record<string, unknown>;
interface Frame { rows: Row[]; meta: Row }

const DAY = 86_400_000;
const iso = (d: Date) => d.toISOString().slice(0, 10);
const parse = (s: string) => new Date(`${s}T00:00:00Z`);
const addDays = (s: string, n: number) => iso(new Date(parse(s).getTime() + n * DAY));
const daysInclusive = (a: string, b: string) => Math.round((parse(b).getTime() - parse(a).getTime()) / DAY) + 1;

/** Inclusive Sentinel local dates -> NomOS half-open instants: local midnights in the tenant's zone (a parameter mapping, not arithmetic on data). */
async function win(start: string, end: string) {
  const zone = await tenantZone();
  return { period_start: localMidnight(start, zone), period_end: localMidnight(addDays(end, 1), zone) };
}

const yearBack = (d: string) => { const x = parse(d); const m = x.getUTCMonth(); x.setUTCFullYear(x.getUTCFullYear() - 1); if (x.getUTCMonth() !== m) x.setUTCDate(0); return iso(x); };
/** Percent change of two NomOS outputs (one division, Sentinel: only when the baseline is > 0). */
const pct = (cur: number | null, base: number | null): number | null => (cur !== null && base !== null && base > 0 ? ((cur - base) / base) * 100 : null);
/** Sentinel's previous window: same number of days immediately before `start`. */
const prevWindow = (start: string, end: string): [string, string] => { const n = daysInclusive(start, end); return [addDays(start, -n), addDays(start, -1)]; };

/**
 * Run a reduce query and follow `meta.page.cursor` until the answer is complete.
 * NomOS pages every answer at 200 rows by default (PageSpec.size, max 1000) - the "200 sites" smoke-test limit.
 */
/** Transport hints for a read (./client): `priority: 'high'` = the read the person is waiting on goes first in its batch. */
type ReadOpts = { priority?: ReadPriority };

async function reduce(scope: Row, reducer: Row, extra: Row = {}, opts: ReadOpts = {}): Promise<Frame> {
  const rows: Row[] = [];
  let meta: Row = {};
  let cursor: string | undefined;
  for (let guard = 0; guard < 200; guard++) {
    const body = { op: 'reduce', scope, reducer, ...extra, page: { size: 10_000, ...(cursor ? { cursor } : {}) } };
    const res = await runQuery<{ rows: Row[]; meta: Row }>(body, opts);
    rows.push(...(res.data?.rows ?? []));
    meta = res.data?.meta ?? {};
    cursor = ((meta.page as Row | undefined)?.cursor as string | null | undefined) ?? undefined;
    if (!cursor) break;
  }
  return { rows, meta };
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

// The answer-note / reason vocabulary (reasonOf, partialNote, noneNote, noDataReason) is ONE copy in Poiesis
// (`@poiesis/views/answerNotes`, SEN-433 T1); this module only applies it per card.
type Unavailable = Record<string, string>;
/** A scalar for one card: null with the reason (refusal, or the no-data operands) in `gone`; a value that leaves a
 *  few entities out carries the note in `partial`. */
async function scalarNoted(quantity: string, w: Row, filter: NomosFilter | undefined, field: string, gone: Unavailable,
  partial?: Unavailable): Promise<number | null> {
  try {
    const f = await reduce({ entity_def: 'Site', quantity, ...w, ...(filter ? { filter } : {}) }, { kind: 'scalar', agg: 'sum' });
    const v = num(f.rows[0]?.value);
    if (v === null) gone[field] = unknownReason({ frame: f });
    else if (partial) { const note = partialNote(f.meta); if (note) partial[field] = note; }
    return v;
  } catch (e) { gone[field] = reasonOf(e); return null; }
}

/** records/query `aggregates`: sum of a record attribute (no group_by = one total row; with group_by = one row per key). */
async function attributeSum(type: string, attribute: string, filter?: NomosFilter, groupBy?: string): Promise<Map<string, number | null>> {
  return new Map([...(await attributeAgg(type, attribute, filter, groupBy))].map(([k, v]) => [k, v.sum]));
}
async function attributeAgg(type: string, attribute: string, filter?: NomosFilter, groupBy?: string): Promise<Map<string, { sum: number | null; count: number }>> {
  const res = await nomosFetch<NomosEnvelope<Array<{ key: string[]; count: number; aggregates: Array<{ value: number | null }> }>>>(
    'POST', `/objects/${type}/records/query`,
    { aggregates: [{ fn: 'sum', attribute }], ...(groupBy ? { group_by: [groupBy] } : {}), ...(filter ? { filter } : {}), limit: 1000 });
  return new Map((res.data ?? []).map((g) => [groupBy ? String(g.key[0]) : '', { sum: num(g.aggregates?.[0]?.value), count: g.count }]));
}

async function scalarOf(quantity: string, w: Row, filter?: NomosFilter, opts: ReadOpts = {}): Promise<number | null> {
  const f = await reduce({ entity_def: 'Site', quantity, ...w, ...(filter ? { filter } : {}) }, { kind: 'scalar', agg: 'sum' }, {}, opts);
  return num(f.rows[0]?.value);
}

/** Sentinel main_meter_only: a meter with no parent meter (2026-10-20: there is no portfolio flag). */
const MAIN_METER: NomosFilter = { attribute: 'parent_meter', op: 'is_not_set', value: null };

/** Sentinel lists/aggregates ACTIVE buildings by default (818 of NomOS's 831 sites): `site.is_active`. */
const ACTIVE: NomosFilter = { attribute: 'is_active', op: 'eq', value: true };
/** Same condition on a Meter scope (rolled to the owning site). */
const METER_SITE_ACTIVE: NomosFilter = { attribute: 'site.is_active', op: 'eq', value: true };

/** Explicit building ids -> that set (as Sentinel); portfolio (no ids) -> active buildings only. */
const buildingFilter = (ids?: string[]): NomosFilter =>
  ids?.length ? { attribute: 'sentinel_building_id', op: 'in', value: ids } : ACTIVE;

// ---------------------------------------------------------------------------------------------------------------
// Record directories (names, areas, ids). Cached per session. records/values keys cells by attribute id.
// ---------------------------------------------------------------------------------------------------------------

interface Rec { id: string; kenning: string; v: Record<string, unknown> }
const dirCache = new Map<string, Promise<Rec[]>>();
export function clearAggregatesCache(): void { dirCache.clear(); }
registerTenantCache(clearAggregatesCache);

/** All records of a type with the given fields (shared client.queryRecords, 1000 per page). Cached per session.
 *  A directory of the same type and filter that already carries every requested field (loaded or loading) is reused. */
function directory(type: string, fields: string[], filter?: NomosFilter): Promise<Rec[]> {
  const f = JSON.stringify(filter ?? null);
  const key = `${type}|${fields.join(',')}|${f}`;
  let p = dirCache.get(key);
  if (!p) {
    for (const [k, q] of dirCache) {
      const [t, fs, ff] = k.split('|');
      const have = new Set(fs.split(','));
      if (t === type && ff === f && fields.every((x) => have.has(x))) return q;
    }
  }
  if (!p) {
    p = (async () => {
      const out: Rec[] = [];
      for (let offset = 0; ; offset += 1000) {
        const page = await queryRecords(type, { filter, fields, limit: 1000, cursor: offset });
        out.push(...page.rows.map((r) => ({ id: r.record_id, kenning: r.kenning, v: r.values })));
        if (page.rows.length < 1000) break;
      }
      return out;
    })();
    p.catch(() => dirCache.delete(key));
    dirCache.set(key, p);
  }
  return p;
}

const refId = (v: unknown): string | undefined => {
  const x = Array.isArray(v) ? v[0] : v;
  return x && typeof x === 'object' ? (x as { target_record_id?: string }).target_record_id : undefined;
};
const str = (v: unknown): string | undefined => { const s = scalar(v); return typeof s === 'string' ? s : undefined; };

// Site directories by need: each method asks only for the attributes it reads (the full set is ~1 MB for 800 sites).
// The active subset is the same directory narrowed by its own is_active cell (a row filter on a fetched attribute, no
// arithmetic), so a page never loads the site directory twice.
const SITE_CORE = ['name', 'sentinel_building_id', 'is_active'];
const siteDir = (fields: string[] = SITE_CORE) => directory('site', fields);
const isActive = (s: Rec) => scalar(s.v.is_active) === true;
const activeSiteDir = async (fields: string[] = SITE_CORE) => (await siteDir(fields)).filter(isActive);
// seu_category was fetched but never read. KPIs read only the fuel vocabulary columns.
const METER_FUEL = ['site', 'energy_source', ...METER_WINDOW_FIELDS];
const meterDir = (fields: string[] = [...METER_FUEL, 'name', 'sentinel_meter_id', 'parent_meter']) => directory('meter', fields);
/** The meters of the given buildings only (by reference path), for a one-building screen. */
const meterDirOf = (buildingIds: string[]) => directory('meter', [...METER_FUEL, 'name', 'sentinel_meter_id', 'parent_meter'],
  { attribute: 'site.sentinel_building_id', op: 'in', value: buildingIds });
const sourceDir = () => directory('energy_source', ['name', 'code']);

async function siteBySid(activeOnly = false, fields: string[] = SITE_CORE): Promise<Map<string, Rec>> {
  const m = new Map<string, Rec>();
  // a site made in NomOS (created in the UI) has no sentinel_building_id: the screens know it by its record id
  for (const s of await (activeOnly ? activeSiteDir(fields) : siteDir(fields))) m.set(str(s.v.sentinel_building_id) ?? s.id, s);
  return m;
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** One site by building id: the directory, else (a site made in NomOS, perhaps after the directory loaded) the
 *  site whose record id it is. Undefined when neither matches. */
async function siteOf(buildingId: string, fields: string[] = SITE_CORE): Promise<Rec | undefined> {
  const hit = (await siteBySid(false, fields)).get(buildingId);
  if (hit || !UUID_RE.test(buildingId)) return hit;
  const { rows } = await queryRecords('site', { filter: { attribute: 'record_id', op: 'eq', value: buildingId }, fields, limit: 1 });
  return rows[0] ? { id: rows[0].record_id, kenning: rows[0].kenning, v: rows[0].values } : undefined;
}
/** Every site and the active ones, from one directory; with `ids`, only those buildings' sites (a one-building
 *  screen must not load the whole directory, ~750 KB for 800 sites). */
async function sitesAndActive(fields: string[] = SITE_CORE, ids?: string[]): Promise<[Map<string, Rec>, Map<string, Rec>]> {
  const all = new Map<string, Rec>();
  if (ids?.length) {
    for (const s of await directory('site', fields, { attribute: 'sentinel_building_id', op: 'in', value: ids })) {
      all.set(str(s.v.sentinel_building_id) ?? s.id, s);
    }
  } else for (const [sid, s] of await siteBySid(false, fields)) all.set(sid, s);
  return [all, new Map([...all].filter(([, s]) => isActive(s)))];
}
async function sourceSlug(): Promise<Map<string, { slug: string; name: string }>> {
  return new Map((await sourceDir()).map((s) => [s.id, { slug: str(s.v.code) ?? s.kenning, name: str(s.v.name) ?? s.kenning }]));
}

/** An hour bucket's `bucket_start` is labelled in the tenant zone (+02:00); Sentinel emits the true UTC instant with a Z. */
const utcHour = (bucketStart: unknown) => new Date(String(bucketStart)).toISOString().replace('.000Z', 'Z');

// ---------------------------------------------------------------------------------------------------------------
// getKPIs  GET /analytics/kpis
// ---------------------------------------------------------------------------------------------------------------
// total_consumption_kwh <- net_kwh; energy_intensity_kwh_sqm <- eui_per_building (Sentinel rounds 4 dp);
// estimated_cost_eur <- estimated_cost_eur; estimated_carbon_kg <- estimated_carbon_kg (per-(country, fuel) factors).
// total_area_sqm <- records/query aggregates sum(size_sqm). energy_intensity_by_source: Site-scope net_kwh / eui_per_building /
// observed_days(max) / net_kwh(count) grouped by energy_source.code; fuels with no consumption (fuel_oil, water) are the
// energy_source vocabulary entries a portfolio-counting meter in scope uses - 0 kWh, null EUI, as Sentinel shows them.
// Comparison: two NET scalars (equal-length preceding days / same dates last year) and one division: the engine's SCALAR
// compare_value is like-for-like (only entities with current data), Sentinel's baseline is the whole portfolio.
// GAP: metered_only (no is_manual filter on the query engine), buildings_count of fuels counts every site with a row (Sentinel:
//      only kWh > 0: fuel_oil 1 vs 0; smallest addition: a `count_positive`/having-before-count aggregation).
const HEADLINE_KPIS = ['total_consumption_kwh', 'energy_intensity_kwh_sqm', 'estimated_cost_eur', 'estimated_carbon_kg'] as const;

async function getKPIs(params: {
  building_ids?: string[]; start_date?: string; end_date?: string; include_comparison?: boolean; metered_only?: boolean;
  onPartial?: (partial: KPIResponse) => void;
}): Promise<KPIResponse> {
  const end = params.end_date ?? iso(new Date());
  const start = params.start_date ?? `${end.slice(0, 8)}01`;
  const w = await win(start, end);
  const filter = buildingFilter(params.building_ids);
  const bySrc = (q: string, agg: string) => reduce({ entity_def: 'Site', quantity: q, ...w, filter },
    { kind: 'group-by', agg, dimensions: ['energy_source.code'] }).catch(() => ({ rows: [] as Row[], meta: {} }));
  // Sentinel comparison: NET kWh of the equal-length preceding day window and of the same dates one year earlier
  // (started with everything else: no waterfall).
  const comparison = params.include_comparison === false ? Promise.resolve([null, null] as Array<number | null>) : Promise.all([
    // a refused comparison window is no comparison (null %), never a failed KPI response
    win(...prevWindow(start, end)).then((pw) => scalarOf(CONSUMPTION, pw, filter)).catch(() => null),
    win(yearBack(start), yearBack(end)).then((yw) => scalarOf(CONSUMPTION, yw, filter)).catch(() => null)]);
  comparison.catch(() => undefined); // awaited below; never an unhandled rejection meanwhile
  // Every card settles on its own: a NULL answer stays NULL with its reason in `unavailable`
  // (a refusal, `factor_coverage_none`, no-data operands); a partial cost/carbon keeps its note in `partial`.
  // Never `?? 0`: an unknown cost is not €0.
  const gone: Unavailable = {};
  const partial: Unavailable = {};
  // Progressive: each headline card is shown the moment its own answer lands (owner, 2026-10-23: "we don't need
  // to wait for the last to show the first"). The final response below is unchanged.
  const early: Partial<KPIData> = {};
  const pending = new Set(HEADLINE_KPIS);
  const settle = (field: (typeof HEADLINE_KPIS)[number]) => (v: number | null) => {
    early[field] = v;
    pending.delete(field);
    if (params.onPartial && pending.size) params.onPartial({
      building_ids: params.building_ids ?? [], start_date: start, end_date: end, metered_only: params.metered_only ?? false,
      kpis: { period_days: daysInclusive(start, end), ...early } as KPIData,
      unavailable: { ...gone }, partial: { ...partial }, pending: [...pending], calculated_at: new Date().toISOString(),
    } as KPIResponse);
    return v;
  };
  const [kwh, eui, cost, carbon, area, sNet, sEui, sDays, sCount, anyDays, sources, sites, meters] = await Promise.all([
    scalarNoted(CONSUMPTION, w, filter, 'total_consumption_kwh', gone).then(settle('total_consumption_kwh')),
    scalarNoted(CONSUMPTION_EUI, w, filter, 'energy_intensity_kwh_sqm', gone).then(settle('energy_intensity_kwh_sqm')),
    scalarNoted('estimated_cost_eur', w, filter, 'estimated_cost_eur', gone, partial).then(settle('estimated_cost_eur')),
    scalarNoted('estimated_carbon_kg', w, filter, 'estimated_carbon_kg', gone, partial).then(settle('estimated_carbon_kg')),
    attributeSum('site', 'size_sqm', filter),
    bySrc(CONSUMPTION, 'sum'), bySrc(CONSUMPTION_EUI, 'sum'), bySrc('observed_days', 'max'), bySrc(CONSUMPTION, 'count'),
    reduce({ entity_def: 'Site', quantity: 'observed_days', ...w, filter }, { kind: 'scalar', agg: 'max' }).then((f) => num(f.rows[0]?.value)).catch(() => null),
    sourceSlug(),
    siteBySid(!params.building_ids?.length),
    params.building_ids?.length
      ? directory('meter', METER_FUEL, { attribute: 'site.sentinel_building_id', op: 'in', value: params.building_ids })
      : meterDir(METER_FUEL),
  ]);
  const scoped = params.building_ids?.length ? params.building_ids.filter((b) => sites.has(b)) : [...sites.keys()];
  const scopedIds = new Set(scoped.map((b) => sites.get(b)!.id));
  const code = (r: Row) => String(r['energy_source.code']);
  const by = (f: Frame) => new Map(f.rows.map((r) => [code(r), num(r.value)]));
  const [netOf, euiOf, daysOf, countOf] = [by(sNet), by(sEui), by(sDays), by(sCount)];
  // Vocabulary join: a fuel a meter in scope uses while it counts (its active window overlaps the period, owner ruling
  // 2026-10-20) but that carries no consumption in the window is listed with 0.
  const meterFuels = new Set(meters.filter((m) => countsInPeriod(m.v, start, end) && scopedIds.has(refId(m.v.site) ?? '')).map((m) => sources.get(refId(m.v.energy_source) ?? '')?.slug).filter(Boolean) as string[]);
  const fuels = new Set([...netOf.keys(), ...meterFuels]);
  fuels.delete('solar_production');
  const slices = [...fuels]
    .map((f) => ({ f, kwh: netOf.get(f) ?? 0 }))
    .sort((a, b) => b.kwh - a.kwh)
    .map(({ f, kwh: k }) => ({ energy_source: f, total_kwh: k, eui_kwh_sqm_year: k ? euiOf.get(f) ?? null : null,
      days_with_data: daysOf.get(f) ?? anyDays ?? 0, buildings_count: k ? countOf.get(f) ?? 0 : 0 }));
  const [pm, py] = await comparison;
  const vsMonth = pct(kwh, pm);
  const vsYear = pct(kwh, py);
  return {
    building_ids: params.building_ids ?? scoped,
    start_date: start, end_date: end, metered_only: params.metered_only ?? false,
    kpis: {
      total_consumption_kwh: kwh,
      energy_intensity_kwh_sqm: eui,
      energy_intensity_by_source: slices,
      estimated_cost_eur: cost,
      estimated_carbon_kg: carbon,
      period_days: daysInclusive(start, end),
      buildings_count: scoped.length,
      total_area_sqm: area.get('') ?? 0,
    },
    comparison: params.include_comparison === false ? undefined : {
      vs_last_month_percent: vsMonth ?? undefined, vs_last_year_percent: vsYear ?? undefined,
      trend: vsMonth !== null && vsMonth > 5 ? 'up' : vsMonth !== null && vsMonth < -5 ? 'down' : 'stable' },
    unavailable: gone,
    partial,
    calculated_at: new Date().toISOString(),
  } as KPIResponse;
}

// ---------------------------------------------------------------------------------------------------------------
// getConsumptionBreakdown  GET /analytics/consumption-breakdown
// ---------------------------------------------------------------------------------------------------------------
// Sentinel's building total = the sum of POSITIVE main-meter kWh (production meters are reported separately as
// onsite_generation_kwh). NomOS: Meter-scope `kwh` grouped by `site`, gated to active sites, main meters (no parent) and
// energy_source != solar_production (a filter, not a sign test). Per-meter total_kwh <- Meter-scope `kwh` by meter; a
// production meter carries no `kwh` and reports minus its `kwh_export` (Sentinel signs generation negative).
// percentage_of_total / percentage_of_parent <- one division of two NomOS meter/building values (Sentinel: only top-level
// positive meters carry a share of the total; sub-meters a share of their parent).
// Every active site and meter is listed (Sentinel LEFT JOINs meters), zero where NomOS has no readings.
// GAP: data_source / quality / reading_count per meter (no data-source attribute on Meter, no measured/manual flag; smallest
//      addition: Meter.data_source ref + a reading-count measure), devices and device_coverage_percent (no device type),
//      portfolio total differs by one meter that Sentinel drops for being negative (positive-only aggregation).
async function getConsumptionBreakdown(params?: { building_ids?: string[]; start_date?: string; end_date?: string }): Promise<ConsumptionBreakdownResponse> {
  const end = params?.end_date ?? iso(new Date());
  const start = params?.start_date ?? `${end.slice(0, 8)}01`;
  const w = await win(start, end);
  // ONE round trip: the queries name the buildings and the solar fuel through reference paths
  // (site.sentinel_building_id, energy_source.code), so they need no directory first; the directories travel beside them.
  const ids = params?.building_ids?.length ? params.building_ids : null;
  const mf: NomosFilter = ids ? { attribute: 'site.sentinel_building_id', op: 'in', value: ids } : METER_SITE_ACTIVE;
  const gross: NomosFilter = { and: [mf, MAIN_METER, { attribute: 'energy_source.code', op: 'ne', value: 'solar_production' }] };
  const q = (scope: Row, dims: string[]) => reduce(scope, { kind: 'group-by', agg: 'sum', dimensions: dims });
  const empty = { rows: [] as Row[], meta: {} };
  const [[sites, active], meters, sources, perMeter, perSite, perExport, genTotal, grossTotal, genBySite] = await Promise.all([
    sitesAndActive(), ids ? meterDirOf(ids) : meterDir(), sourceSlug(),
    q({ entity_def: 'Meter', quantity: 'kwh', ...w, filter: mf }, ['site_id']),
    q({ entity_def: 'Meter', quantity: 'kwh', ...w, filter: gross }, ['site']),
    q({ entity_def: 'Meter', quantity: 'kwh_export', ...w, filter: mf }, ['site_id']).catch(() => empty),
    reduce({ entity_def: 'Meter', quantity: 'kwh_export', ...w, filter: mf }, { kind: 'scalar', agg: 'sum' }).then((f) => num(f.rows[0]?.value)).catch(() => null),
    reduce({ entity_def: 'Meter', quantity: 'kwh', ...w, filter: gross }, { kind: 'scalar', agg: 'sum' }).then((f) => num(f.rows[0]?.value)).catch(() => null),
    // generation per building: a production meter's export, rolled to its site by the `site` dimension
    q({ entity_def: 'Meter', quantity: 'kwh_export', ...w, filter: mf }, ['site']).catch(() => empty),
  ]);
  const wanted = (ids ?? [...active.keys()]).filter((b) => sites.has(b));
  const sid = new Map(wanted.map((b) => [sites.get(b)!.id, b]));
  const kwhOf = new Map(perMeter.rows.map((r) => [String(r.site_id), num(r.value) ?? 0]));
  const exportOf = new Map(perExport.rows.map((r) => [String(r.site_id), num(r.value) ?? 0]));
  const totalOf = new Map(perSite.rows.map((r) => [String(r.site), num(r.value) ?? 0]));
  const genOf = new Map(genBySite.rows.map((r) => [String(r.site), Math.abs(num(r.value) ?? 0)]));
  const sidOfMeter = new Map(meters.map((m) => [m.id, str(m.v.sentinel_meter_id) ?? m.id]));
  const meterBySite = new Map<string, typeof meters>();
  for (const m of meters) { const sr = refId(m.v.site); if (sr && sid.has(sr)) meterBySite.set(sr, [...(meterBySite.get(sr) ?? []), m]); }
  const meterKwh = (m: Rec, slug: string | undefined) => kwhOf.get(m.id) ?? (slug === 'solar_production' && exportOf.has(m.id) ? -(exportOf.get(m.id) as number) : 0);
  const share = (part: number, whole: number) => (part > 0 && whole > 0 ? (part / whole) * 100 : 0);
  const buildings = [...sid.entries()].map(([rid, bid]) => {
    const s = sites.get(bid)!;
    const total = totalOf.get(rid) ?? 0;
    const own = meterBySite.get(rid) ?? [];
    const kwhByMeter = new Map(own.map((m) => [m.id, meterKwh(m, sources.get(refId(m.v.energy_source) ?? '')?.slug)]));
    const mp = own.map((m) => {
      const src = sources.get(refId(m.v.energy_source) ?? '');
      const parent = refId(m.v.parent_meter);
      const k = kwhByMeter.get(m.id) ?? 0;
      return {
        meter_point_id: str(m.v.sentinel_meter_id) ?? m.id, meter_point_name: str(m.v.name) ?? m.kenning,
        meter_type: src?.slug ?? '', energy_source: src?.slug ?? null, total_kwh: k,
        percentage_of_total: parent ? 0 : share(k, total), parent_meter_id: parent ? sidOfMeter.get(parent) ?? null : null,
        percentage_of_parent: parent ? ((kwhByMeter.get(parent) ?? 0) > 0 ? (k / (kwhByMeter.get(parent) as number)) * 100 : 0) : 0,
        data_source: null as string | null, quality: '', reading_count: 0,
      };
    });
    return {
      building_id: bid, building_name: str(s.v.name) ?? s.kenning, start_date: start, end_date: end,
      total_kwh: total, onsite_generation_kwh: genOf.get(rid) ?? 0,
      data_source_type: 'meter_point', meter_points: mp, devices: [], devices_total_kwh: 0, device_coverage_percent: null,
      calculated_at: new Date().toISOString(),
    };
  }).sort((a, b) => (a.building_id < b.building_id ? -1 : 1));
  return { start_date: start, end_date: end, buildings, portfolio_total_kwh: grossTotal ?? 0,
    portfolio_onsite_generation_kwh: Math.abs(genTotal ?? 0), calculated_at: new Date().toISOString() } as unknown as ConsumptionBreakdownResponse;
}

// ---------------------------------------------------------------------------------------------------------------
// getPeriodSelectorSeries  GET /analytics/period-selector-series
// ---------------------------------------------------------------------------------------------------------------
// bars <- Site-scope `kwh` (the portfolio series, equals Sentinel's weekly bars on every week where both sides hold the readings:
// a Meter-scope gross read (main meters, production excluded, with or without the active gate) was tried and is WORSE: 163 weeks
// off, e.g. +2,430 kWh in week 2026-09-21 where Site `kwh` matches to the cent) time-bucket by ISO week over the whole history.
// Remaining 92 weeks (2024-08..2026-05, Sentinel higher by a constant 757-5,300 kWh/week) are readings Sentinel holds that the
// migration did not carry (a data gap, not an engine gap).
// available_range <- first/last data day, read from one-week day-grain windows at the two ends of the weekly series.
async function getPeriodSelectorSeries(params: { buildingId?: string } = {}): Promise<PeriodSelectorSeriesResponse> {
  const filter = buildingFilter(params.buildingId ? [params.buildingId] : undefined);
  const w = await win('2000-01-01', iso(new Date()));
  const scope = { entity_def: 'Site', quantity: 'kwh', ...w, ...(filter ? { filter } : {}) };
  const weeks = await reduce(scope, { kind: 'time-bucket', agg: 'sum', granularity: 'week', dimensions: [] });
  const bars = weeks.rows.filter((r) => num(r.value) !== null)
    .map((r) => ({ week_start: String(r.bucket_date), total_kwh: num(r.value) as number }))
    .sort((a, b) => a.week_start.localeCompare(b.week_start));
  if (!bars.length) return { available_range: null, bars: [] };
  // Exact first/last data day: two one-week day-grain reads at the ends of the series. A second round trip, kept on
  // purpose: a whole-history day series in the first round costs ~3 s cold on ClickHouse (measured, fan-in-2).
  const edge = async (weekStart: string) => (await reduce(
    { ...scope, ...(await win(weekStart, addDays(weekStart, 6))) }, { kind: 'time-bucket', agg: 'sum', granularity: 'day', dimensions: [] }))
    .rows.filter((r) => num(r.value) !== null).map((r) => String(r.bucket_date)).sort();
  const [head, tail] = await Promise.all([edge(bars[0].week_start), edge(bars[bars.length - 1].week_start)]);
  return {
    available_range: { start: head[0] ?? bars[0].week_start, end: tail[tail.length - 1] ?? bars[bars.length - 1].week_start },
    bars,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Weather  GET /weather/{id}/history, POST /weather/history/batch, GET /weather/{id}/forecast
// ---------------------------------------------------------------------------------------------------------------
// Weather is a NomOS telemetry source (monad/weather): hourly `temperature` (C), `humidity` (%), `wind_speed` (m/s) and a
// daily `actual_hdd` per Site. History = Site-scope time-bucket of those quantities (temperature avg/min/max, humidity avg,
// wind avg), bucket = UTC hour / local day. Parity attributions (all Sentinel defects, NomOS is the correct side):
//  - Sentinel stores local clock time labelled as UTC, so its HOURLY bucket labels run +1/+2 h ahead of the true UTC hour
//    (daily buckets are unaffected). NomOS returns true UTC instants; the parity diff re-labels (diff.ts WEATHER_HOURLY).
//  - Sentinel's `avg_wind_speed` is km/h in a field documented m/s (x 3.6); NomOS returns m/s.
// GAP: reading_count (readings per bucket - no count measure on weather quantities), forecast / current_weather_code /
//      current_feels_like / wind_direction (Sentinel calls Open-Meteo live; NomOS stores observations only).
//      Smallest NomOS addition: a `weather_forecast` series (daily tmax/tmin/code/precip-prob/wind-max) written by sync_weather.
const WEATHER_PIECES = [
  { field: 'avg_temperature', quantity: 'temperature', agg: 'avg' }, { field: 'min_temperature', quantity: 'temperature', agg: 'min' },
  { field: 'max_temperature', quantity: 'temperature', agg: 'max' }, { field: 'avg_humidity', quantity: 'humidity', agg: 'avg' },
  { field: 'avg_wind_speed', quantity: 'wind_speed', agg: 'avg' },
] as const;

const HOUR = 3_600_000;
const naive = (d: Date) => d.toISOString().slice(0, 19);
/** Sentinel parses start/end as datetimes; an end at exactly 00:00:00 is widened to 23:59:59 of that day. */
function weatherRange(p: { start_date?: string; end_date?: string }) {
  // naive datetimes (Sentinel: no zone) are read as UTC instants
  const dt = (x: string) => new Date(/(Z|[+-]\d\d:\d\d)$/.test(x) ? x : `${x.length === 10 ? `${x}T00:00:00` : x}Z`);
  let end = p.end_date ? dt(p.end_date) : new Date();
  const start = p.start_date ? dt(p.start_date) : new Date(end.getTime() - 7 * DAY);
  if (end.getTime() % DAY === 0) end = new Date(end.getTime() + DAY - 1000);
  return { start, end };
}

/** `siteIds` are site RECORD ids (the frame's site_id), so a site made in NomOS (no Sentinel id) is covered too. */
async function weatherSeries(siteIds: string[], start: Date, end: Date, hourly: boolean, opts: ReadOpts = {}) {
  // hour: whole-hour instants. day: the local calendar days that hold the requested UTC dates (Sentinel's dates are naive).
  const w = hourly
    ? { period_start: new Date(Math.floor(start.getTime() / HOUR) * HOUR).toISOString(),
      period_end: new Date((Math.floor(end.getTime() / HOUR) + 1) * HOUR).toISOString() }
    : await win(iso(start), iso(end));
  const filter: NomosFilter = { attribute: 'record_id', op: 'in', value: siteIds };
  const frames = await Promise.all(WEATHER_PIECES.map((x) => reduce({ entity_def: 'Site', quantity: x.quantity, ...w, filter },
    { kind: 'time-bucket', agg: x.agg, granularity: hourly ? 'hour' : 'day', dimensions: ['site_id'] }, {}, opts)));
  const out = new Map<string, Map<string, Partial<WeatherHistoryPoint>>>();
  WEATHER_PIECES.forEach((x, i) => {
    for (const r of frames[i].rows) {
      const v = num(r.value);
      if (v === null) continue;
      const key = hourly ? utcHour(r.bucket_start) : `${String(r.bucket_date)}T00:00:00Z`;
      const bySite = out.get(String(r.site_id)) ?? new Map();
      out.set(String(r.site_id), bySite);
      bySite.set(key, { ...(bySite.get(key) ?? {}), [x.field]: v });
    }
  });
  return out;
}

const notFound = (id: string) => toAxiosError(404, { detail: `Building ${id} not found. Verify the UUID is correct.` }, `/weather/${id}`);

async function weatherHistories(ids: string[], p: { start_date?: string; end_date?: string; aggregation?: string }, dflt: string,
  opts: ReadOpts = {}) {
  const aggregation = p.aggregation ?? dflt;
  if (aggregation !== 'hourly' && aggregation !== 'daily') throw toAxiosError(400, { detail: "Aggregation must be 'hourly' or 'daily'" }, '/weather');
  const { start, end } = weatherRange(p);
  const recs = await Promise.all(ids.map((b) => siteOf(b)));
  const sites = new Map(ids.flatMap((b, i) => (recs[i] ? [[b, recs[i]!] as const] : [])));
  const found = ids.filter((b) => sites.has(b));
  const series = found.length ? await weatherSeries(found.map((b) => sites.get(b)!.id), start, end, aggregation === 'hourly', opts) : new Map();
  const results: WeatherHistoryResponse[] = found.map((b) => {
    const rec = sites.get(b)!;
    const data = [...(series.get(rec.id) ?? new Map<string, Partial<WeatherHistoryPoint>>()).entries()]
      .sort(([x], [y]) => (x < y ? -1 : 1))
      .map(([bucket, v]) => ({ bucket, reading_count: 0, ...v }) as WeatherHistoryPoint);
    return { building_id: b, building_name: str(rec.v.name) ?? rec.kenning, start_date: naive(start), end_date: naive(end),
      aggregation, data, total_points: data.length };
  });
  return { results, missing: ids.filter((b) => !sites.has(b)) };
}

/** `opts.priority: 'high'`: the chart's temperature overlay, read with the chart (see getEnergyAnalytics). */
async function getWeatherHistory(buildingId: string, params?: { start_date?: string; end_date?: string; aggregation?: 'hourly' | 'daily' },
  opts: ReadOpts = {}): Promise<WeatherHistoryResponse> {
  const { results } = await weatherHistories([buildingId], params ?? {}, 'hourly', opts);
  if (!results.length) throw notFound(buildingId);
  return results[0];
}

// ---------------------------------------------------------------------------------------------------------------
// Not ported (named NomOS gaps, see report): exportQualityFixList (later: the data-quality view, the same
// file read as exportAnalytics).
// getQualityMetrics lives in impl/quality.ts.
// ---------------------------------------------------------------------------------------------------------------

export const aggregatesImpl: NomosRegistry = {
  getKPIs: getKPIs as unknown as AnyFn,
  getConsumptionBreakdown: getConsumptionBreakdown as unknown as AnyFn,
  getPeriodSelectorSeries: getPeriodSelectorSeries as unknown as AnyFn,
  getWeatherHistory: getWeatherHistory as unknown as AnyFn,
};
